import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

// A complete US shipping address must have a street number, street name,
// city, state and a 5-digit ZIP code before a purchase can be submitted.
function extractAddressParts(suggestion) {
  const a = (suggestion && suggestion.address) || {};
  return {
    houseNumber: a.house_number || '',
    road: a.road || '',
    city: a.city || a.town || a.village || a.hamlet || '',
    state: a.state || '',
    zip: a.postcode || '',
  };
}

function isCompleteAddress(parts) {
  if (!parts) return false;
  return Boolean(
    parts.houseNumber &&
    parts.road &&
    parts.city &&
    parts.state &&
    /^\d{5}(-\d{4})?$/.test(parts.zip || '')
  );
}

function Modal({ title, children, onClose, actions }) {
  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="modal-title">{title}</h3>
        <div className="modal-body">{children}</div>
        <div className="modal-actions">
          {actions || <button type="button" className="btn btn-primary" onClick={onClose}>OK</button>}
        </div>
      </div>
    </div>
  );
}

export default function ShopPage() {
  const { user } = useAuth();
  const [sections, setSections] = useState([]);
  const [paddles, setPaddles] = useState([]);
  const [credits, setCredits] = useState([]);
  const [creditEligibleItemIds, setCreditEligibleItemIds] = useState([]);
  const [creditPopupMessage, setCreditPopupMessage] = useState('');
  const [taxConfig, setTaxConfig] = useState({ ratePercent: 6, state: 'michigan' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchItems() {
      try {
        const res = await fetch('/api/paddles', { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to load shop items');
        const data = await res.json();
        if (Array.isArray(data)) {
          setPaddles(data);
          setSections([]);
        } else {
          setPaddles(data.paddles || []);
          setSections(data.sections || []);
          setCredits(data.credits || []);
          setCreditEligibleItemIds(data.creditEligibleItemIds || []);
          setCreditPopupMessage(data.creditPopupMessage || '');
          setTaxConfig({
            ratePercent: data.taxRatePercent ?? 6,
            state: (data.taxState || 'michigan').toLowerCase(),
          });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchItems();
  }, []);

  if (loading) return <div className="page"><div className="loading">Loading shop...</div></div>;
  if (error) return <div className="page"><div className="alert alert-error">{error}</div></div>;

  const sortPaddles = (paddleList, sortField, sortDirection) => {
    const sorted = [...paddleList].sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'sortOrder') {
        aVal = a.sortOrder || 0;
        bVal = b.sortOrder || 0;
      } else if (sortField === 'tier') {
        aVal = a.tier || '';
        bVal = b.tier || '';
      } else {
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const sectionedPaddleIds = new Set();
  const sectionContent = sections.map(section => {
    const sectionPaddles = paddles.filter(p => p.sectionId === section.id);
    sectionPaddles.forEach(p => sectionedPaddleIds.add(p.id));
    const sorted = sortPaddles(sectionPaddles, section.sortField || 'name', section.sortDirection || 'asc');
    return { section, paddles: sorted };
  }).filter(s => s.paddles.length > 0);

  const unsectionedPaddles = paddles.filter(p => !sectionedPaddleIds.has(p.id));

  const renderCard = (paddle) => (
    <ShopItemCard
      key={paddle.id}
      paddle={paddle}
      user={user}
      credits={credits}
      creditEligible={creditEligibleItemIds.includes(paddle.id)}
      creditPopupMessage={creditPopupMessage}
      taxConfig={taxConfig}
      onCreditsChange={setCredits}
    />
  );

  if (paddles.length === 0) {
    return (
      <div className="page paddles-page">
        <h1>Shop</h1>
        <p className="empty-state">No items available for you right now.</p>
      </div>
    );
  }

  return (
    <div className="page paddles-page">
      <h1>Shop</h1>
      {sectionContent.map(({ section, paddles: sectionPaddles }) => (
        <div key={section.id} className="paddle-section">
          <h2 className="paddle-section-title">{section.name}</h2>
          <div className="paddle-grid">
            {sectionPaddles.map(renderCard)}
          </div>
        </div>
      ))}
      {unsectionedPaddles.length > 0 && sectionContent.length > 0 && (
        <div className="paddle-section">
          <h2 className="paddle-section-title">Other</h2>
          <div className="paddle-grid">
            {unsectionedPaddles.map(renderCard)}
          </div>
        </div>
      )}
      {unsectionedPaddles.length > 0 && sectionContent.length === 0 && (
        <div className="paddle-grid">
          {unsectionedPaddles.map(renderCard)}
        </div>
      )}
    </div>
  );
}

function ShopItemCard({ paddle, user, credits = [], creditEligible = false, creditPopupMessage = '', taxConfig = { ratePercent: 6, state: 'michigan' }, onCreditsChange }) {
  const [deliveryMethod, setDeliveryMethod] = useState('shipping');
  const [shippingAddress, setShippingAddress] = useState('');
  const [addressParts, setAddressParts] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCreditId, setSelectedCreditId] = useState('');
  const [showAddressError, setShowAddressError] = useState(false);
  const [showCreditNotice, setShowCreditNotice] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);
  const suggestionsRef = useRef(null);

  const availableCredits = creditEligible ? credits : [];
  const usableCredits = availableCredits.filter((c) => !c.used);
  const selectedCredit = usableCredits.find((c) => c.id === selectedCreditId) || null;

  const fetchSuggestions = useCallback((query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=5&countrycodes=us`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 300);
  }, []);

  const formatShortAddress = (suggestion) => {
    const p = extractAddressParts(suggestion);
    const street = [p.houseNumber, p.road].filter(Boolean).join(' ');
    return [street, p.city, [p.state, p.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  };

  const handleAddressChange = (e) => {
    const val = e.target.value;
    setShippingAddress(val);
    // Typing invalidates any previously confirmed address
    setAddressParts(null);
    fetchSuggestions(val);
  };

  const handleSuggestionClick = (suggestion) => {
    const parts = extractAddressParts(suggestion);
    setShippingAddress(formatShortAddress(suggestion));
    setAddressParts(parts);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const addressComplete = isCompleteAddress(addressParts);
  const needsAddress = deliveryMethod === 'shipping';
  const addressOk = !needsAddress || addressComplete;

  const handleCreditChange = (e) => {
    const value = e.target.value;
    setSelectedCreditId(value);
    if (value && creditPopupMessage) setShowCreditNotice(true);
  };

  // ─── Cost breakdown ───
  const getBreakdown = () => {
    const basePrice = Number(paddle.price) || 0;
    const shipping = (paddle.custom && deliveryMethod === 'shipping') ? (Number(paddle.shippingPrice) || 0) : 0;
    // Sales tax applies to local pickup or to shipments inside the tax state
    const applyTax =
      deliveryMethod === 'pickup' ||
      (addressComplete && (addressParts.state || '').toLowerCase() === taxConfig.state);
    const taxRate = (Number(taxConfig.ratePercent) || 0) / 100;

    // The credit comes off the base price FIRST, so sales tax is charged on the
    // discounted amount. Anything left over is then applied to shipping.
    const creditAmount = selectedCredit ? Number(selectedCredit.amount) || 0 : 0;
    const creditToBase = Math.min(creditAmount, basePrice);
    const taxableAmount = basePrice - creditToBase;
    const tax = applyTax ? taxableAmount * taxRate : 0;
    const creditToShipping = Math.min(creditAmount - creditToBase, shipping);
    const creditApplied = creditToBase + creditToShipping;

    const total = Math.max(0, taxableAmount + tax + shipping - creditToShipping);
    return { subtotal: basePrice, tax, shipping, creditApplied, total };
  };

  const breakdown = getBreakdown();

  const getCalculatedValues = () => {
    if (!paddle.custom) return { amount: null, note: null };

    const amount = breakdown.total;

    const nameParts = (user?.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const addressOrPickup = deliveryMethod === 'pickup' ? 'pickup' : shippingAddress;
    const note = [user?.email || '', paddle.paddleShortName || '', firstName, lastName, addressOrPickup].join('+');

    return { amount: amount.toFixed(2), note };
  };

  const { amount, note } = getCalculatedValues();

  const buildPurchaseUrl = () => {
    if (!paddle.purchaseUrl || !amount || !note) return null;
    return `${paddle.purchaseUrl}&amount=${amount}&note=${encodeURIComponent(note)}`;
  };

  // Records the purchase server-side (redeems the credit and notifies support
  // for referred users). Returns false when the credit could not be redeemed.
  const recordPurchase = async (purchaseAmount) => {
    try {
      const res = await fetch('/api/purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemId: paddle.id,
          itemName: paddle.name,
          creditId: selectedCredit ? selectedCredit.id : null,
          amount: purchaseAmount,
        }),
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Could not complete the purchase.';
        try { msg = JSON.parse(text).error || msg; } catch {}
        setPurchaseError(msg);
        return false;
      }
      const data = await res.json();
      if (Array.isArray(data.credits) && onCreditsChange) {
        onCreditsChange(data.credits);
        setSelectedCreditId('');
      }
      return true;
    } catch {
      // A failed notification must not block the purchase itself
      return true;
    }
  };

  const openVenmo = (webUrl) => {
    const appUrl = webUrl.replace(/^https:\/\/venmo\.com\//, 'venmo://');

    const fallbackTimeout = setTimeout(() => {
      window.open(webUrl, '_blank', 'noopener,noreferrer');
    }, 1500);

    window.addEventListener('blur', function onBlur() {
      clearTimeout(fallbackTimeout);
      window.removeEventListener('blur', onBlur);
    });

    window.location.href = appUrl;
  };

  const handlePurchaseClick = async (e) => {
    e.preventDefault();
    setPurchaseError('');

    if (!addressOk) {
      setShowAddressError(true);
      return;
    }

    const webUrl = buildPurchaseUrl();
    if (!webUrl) return;

    setSubmitting(true);
    const ok = await recordPurchase(amount);
    setSubmitting(false);
    if (!ok) return;

    openVenmo(webUrl);
  };

  // Non-custom items link straight out, but still need to record the purchase
  // so credits are redeemed and referral notifications are sent.
  const handleSimplePurchaseClick = async (e) => {
    e.preventDefault();
    setPurchaseError('');
    setSubmitting(true);
    const ok = await recordPurchase(null);
    setSubmitting(false);
    if (!ok) return;
    window.open(paddle.purchaseUrl, '_blank', 'noopener,noreferrer');
  };

  const money = (n) => `$${Number(n).toFixed(2)}`;

  const creditSelector = availableCredits.length > 0 && (
    <div className="credit-field">
      <label htmlFor={`credit-${paddle.id}`}>Apply a credit</label>
      <select id={`credit-${paddle.id}`} value={selectedCreditId} onChange={handleCreditChange}>
        <option value="">— No credit —</option>
        {availableCredits.map((c) => (
          <option key={c.id} value={c.id} disabled={c.used}>
            {money(c.amount)}{c.label ? ` — ${c.label}` : ''}{c.used ? ' (used)' : ''}
          </option>
        ))}
      </select>
      <small className="credit-hint">Only one credit can be applied, and each credit can be used once.</small>
    </div>
  );

  const modals = (
    <>
      {showAddressError && (
        <Modal title="Complete address required" onClose={() => setShowAddressError(false)}>
          <p>
            Please enter and select a complete shipping address — including street number,
            street name, city, state and a 5-digit ZIP code — before purchasing.
          </p>
          <p>Start typing your address and pick a match from the suggestion list.</p>
        </Modal>
      )}
      {showCreditNotice && (
        <Modal title="Credit applied" onClose={() => setShowCreditNotice(false)}>
          <p>{creditPopupMessage}</p>
        </Modal>
      )}
    </>
  );

  return (
    <div className="paddle-card">
      {paddle.imageUrl && (
        <div className="paddle-image">
          <img src={paddle.imageUrl} alt={paddle.name} />
        </div>
      )}
      <div className="paddle-info">
        <h3>{paddle.name}</h3>
        {paddle.description && <p>{paddle.description}</p>}
        {paddle.price != null && (
          <p className="paddle-price">{money(paddle.price)}</p>
        )}
      </div>

      {purchaseError && <div className="alert alert-error">{purchaseError}</div>}

      {paddle.custom ? (
        <div className="custom-purchase-form">
          <div className="delivery-options">
            <label className="radio-label">
              <input
                type="radio"
                name={`delivery-${paddle.id}`}
                value="shipping"
                checked={deliveryMethod === 'shipping'}
                onChange={() => setDeliveryMethod('shipping')}
              />
              Shipping
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name={`delivery-${paddle.id}`}
                value="pickup"
                checked={deliveryMethod === 'pickup'}
                onChange={() => setDeliveryMethod('pickup')}
              />
              Pickup
            </label>
          </div>

          {deliveryMethod === 'shipping' && (
            <div className="address-field" ref={suggestionsRef}>
              <label htmlFor={`address-${paddle.id}`}>Shipping Address</label>
              <input
                id={`address-${paddle.id}`}
                type="text"
                className={shippingAddress && !addressComplete ? 'input-invalid' : ''}
                value={shippingAddress}
                onChange={handleAddressChange}
                placeholder="Start typing your address..."
                autoComplete="off"
                aria-invalid={Boolean(shippingAddress) && !addressComplete}
              />
              {showSuggestions && (
                <ul className="address-suggestions">
                  {suggestions.map((s) => (
                    <li key={s.place_id} onClick={() => handleSuggestionClick(s)}>
                      {formatShortAddress(s)}
                    </li>
                  ))}
                </ul>
              )}
              {!addressComplete && (
                <small className="address-warning">
                  Select a suggestion with a street number, city, state and 5-digit ZIP code.
                </small>
              )}
              <small className="nominatim-attribution">
                Address data © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors
              </small>
            </div>
          )}

          {creditSelector}

          {paddle.price != null && (
            <div className="cost-breakdown">
              <div className="cost-row"><span>Subtotal</span><span>{money(breakdown.subtotal)}</span></div>
              {breakdown.creditApplied > 0 && (
                <div className="cost-row cost-credit"><span>Credit</span><span>-{money(breakdown.creditApplied)}</span></div>
              )}
              {breakdown.tax > 0 && (
                <div className="cost-row"><span>Sales Tax ({taxConfig.ratePercent}%)</span><span>{money(breakdown.tax)}</span></div>
              )}
              {deliveryMethod === 'shipping' && (
                <div className="cost-row"><span>Shipping</span><span>{money(breakdown.shipping)}</span></div>
              )}
              <div className="cost-row cost-total"><span>Total</span><span>{money(breakdown.total)}</span></div>
            </div>
          )}

          {paddle.purchaseUrl && (
            <button
              type="button"
              className="btn btn-primary btn-full"
              onClick={handlePurchaseClick}
              disabled={submitting}
            >
              {submitting ? 'Processing...' : 'Purchase'}
            </button>
          )}

          {addressOk && amount && note && (
            <div className="purchase-fallback">
              <p className="fallback-message">
                If the purchase button doesn't work, please enter the following values in Venmo to payment to @ridgesports
              </p>
              <div className="fallback-values">
                <div className="fallback-row">
                  <span className="fallback-label">Amount:</span>
                  <span className="fallback-value">{amount}</span>
                </div>
                <div className="fallback-row">
                  <span className="fallback-label">Note:</span>
                  <span className="fallback-value">{note}</span>
                </div>
              </div>
            </div>
          )}
          {modals}
        </div>
      ) : (
        <>
          {creditSelector}
          {paddle.purchaseUrl && (
            <button
              type="button"
              className="btn btn-primary btn-full"
              onClick={handleSimplePurchaseClick}
              disabled={submitting}
            >
              {submitting ? 'Processing...' : 'Purchase'}
            </button>
          )}
          {modals}
        </>
      )}
    </div>
  );
}
